import { FC, RefObject } from 'react';
import { Box, Stack } from '@mui/material';
import { createUseStyles } from 'react-jss';
import FlatButton from '@/components/flat-button';
import MessageTray from '../message-tray';
import Icon from '@/components/icon';
import Text from '@/components/text';
import Loader from '@/components/loader';
import useThread from '@/hooks/use-thread.hook';
import useMessageTray from '@/hooks/use-message-tray.hook';
import useUserSettings from '@/hooks/use-user-settings.hook';
import { addAction } from '@/lib/datadog';
import AgentSelect from '@/components/agent-select';
import PrivateModelSelect from '@/components/private-model-select';
import DatasourceList from './datasource-list';
import { getBreakpoint } from '@/theme';
import MessageTrayFooter from '../message-tray/footer';

interface Props {
  containerRef: RefObject<HTMLDivElement>;
}

const useStyles = createUseStyles({
  header: {
    margin: '10px 30px 20px 30px',
    width: 'calc(100% - 60px)',
  },
  settingsContent: {
    margin: '0px 30px',
    width: 'calc(100% - 60px)',
    '& .MuiFormControl-root': {
      marginTop: 0,
    },
  },
  agentModelSelect: {
    maxWidth: 350,
  },
  headerIcon: {
    marginLeft: -3,
  },
  datasourceCard: {
    marginTop: 0,
  },
  [getBreakpoint('md')]: {
    settingsContent: {
      margin: '0px 10px',
      width: 'calc(100% - 20px)',
      flexDirection: 'column',
      gap: 22,
    },
    agentModelSelect: {
      width: '100%',
      maxWidth: '100%', // redundant, but wipes out the above max-width settings
    },
    datasourceCard: {
      width: '100%',
    },
  },
});

interface Props {
  containerRef: RefObject<HTMLDivElement>;
  disabled?: boolean;
}

const ThreadSettingsTray: FC<Props> = ({ containerRef, disabled = false }) => {
  const styles = useStyles();

  const {
    settings,
    setPrivateModelId: setThreadPrivateModelId,
    setAgentId: setThreadAgentId,
    thread,
  } = useThread();

  const { isOpen, toggle } = useMessageTray();
  const open = isOpen('controls');

  const { isLoading } = useUserSettings();

  const handleToggleTray = () => {
    if (!open) {
      addAction('thread_controls_open', { threadId: thread.id });
    }
    toggle('controls');
  };

  const { privateModelId, agentId, isDefaultAgent } = settings;

  return (
    <Box>
      <MessageTray containerRef={containerRef} trayId="controls">
        {isLoading && (
          <Box width="100%" pt={2}>
            <Loader />
          </Box>
        )}
        {!isLoading && (
          <Stack
            direction="column"
            alignItems="flex-start"
            justifyContent="flex-start"
            width="100%"
            height="100%"
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              justifyContent="flex-start"
              className={styles.header}
            >
              <Icon name="settings" className={styles.headerIcon} />
              <Text bold>Chat Settings</Text>
            </Stack>

            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-start"
              gap={6}
              flexGrow={1}
              className={styles.settingsContent}
            >
              <DatasourceList className={styles.datasourceCard} />

              <Stack direction="column" gap={2} flexGrow={1} className={styles.agentModelSelect}>
                <Box width="100%">
                  <AgentSelect value={agentId} onChange={setThreadAgentId} />
                </Box>

                <Box width="100%">
                  {isDefaultAgent && (
                    <PrivateModelSelect onChange={setThreadPrivateModelId} value={privateModelId} />
                  )}
                </Box>
              </Stack>
            </Stack>
            <MessageTrayFooter />
          </Stack>
        )}
      </MessageTray>

      <Stack direction="row" alignItems="center" justifyContent="flex-start">
        <FlatButton
          icon="settings"
          label="Settings"
          onClick={handleToggleTray}
          size="small"
          disabled={disabled}
        />
      </Stack>
    </Box>
  );
};

export default ThreadSettingsTray;
